import { showError, showSuccess } from '@calendly/alert'
import { useBooleanState } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

import { useGetOrganizationUuid, useGetUserQuery } from 'common'
import {
  isRecapPublic,
  normalizeRecapAccessLevel,
  RECAP_ACCESS_LEVELS,
  RecapPermission,
} from 'features/recaps/constants'
import {
  buildNewRecipientsPayload,
  countEffectiveRecipients,
  organizationRelationToDisplay,
  relationToDisplay,
  suggestionToPendingRecipient,
} from 'features/recaps/modals/share_modal/utils'

import { emptyPendingPermissions, emptyPendingRecipients } from './constants'
import { useAccessLevel } from './use_access_level'
import { useAccessList } from './use_access_list'
import { useSubmitPermissions } from './use_submit_permissions'

export { emptyPendingPermissions } from './constants'

const RecapShareContext = createContext(null)

export const useRecapShare = () => {
  const context = useContext(RecapShareContext)

  if (!context) {
    throw new Error('useRecapShare must be used within a RecapShareProvider')
  }

  return context
}

export const RecapShareProvider = ({
  children,
  trackedMeeting,
  recapPermission,
  recapShare = null,
  isRecapShareLoading = false,
  owner = null,
}) => {
  const dispatch = useDispatch()
  const orgUuid = useGetOrganizationUuid()
  const { data: currentUser } = useGetUserQuery()
  const trackedMeetingUuid = trackedMeeting?.meetingIntelligence?.uuid
  const ownerUserUuid = owner?.uuid
  const [isShareModalOpen, openShareModal, closeShareModal] = useBooleanState(false)
  const [isEmailModalOpen, openEmailModal, closeEmailModal] = useBooleanState(false)
  const [pendingPermissionUpdates, setPendingPermissionUpdates] = useState(emptyPendingPermissions)
  const [pendingRecipients, setPendingRecipients] = useState(emptyPendingRecipients)
  const [localShare, setLocalShare] = useState(undefined)

  const hasLocalShareOverride = localShare !== undefined
  const share = hasLocalShareOverride ? localShare : recapShare
  const isShareReady = !isRecapShareLoading && recapPermission !== RecapPermission.VIEW

  useEffect(() => {
    setLocalShare(undefined)
  }, [recapShare])

  const accessLevel = useAccessLevel({
    trackedMeetingUuid,
    recapPermission,
    share,
    isShareModalOpen,
    isShareReady,
    setLocalShare,
  })

  const {
    accessList,
    recipientCount,
    setRecipientCount,
    refetch,
    applyPostMutationResult,
    applyDeleteMutationResult,
  } = useAccessList({
    isShareModalOpen,
    accessLevel: accessLevel.value,
    trackedMeetingUuid,
    recapPermission,
    owner,
    currentUser,
    orgUuid,
  })

  const { submit: submitPermissions, isSubmitting: isSubmittingPermissions } = useSubmitPermissions({
    trackedMeetingUuid,
    pendingPermissionUpdates,
    pendingRecipients,
    accessList,
    setPendingPermissionUpdates,
    setPendingRecipients,
    applyPostMutationResult,
    applyDeleteMutationResult,
  })

  // Clear pending permissions and recipients on modal close.
  useEffect(() => {
    if (!isShareModalOpen) {
      setPendingPermissionUpdates(emptyPendingPermissions)
      setPendingRecipients(emptyPendingRecipients)
    }
  }, [isShareModalOpen])

  // Clear pending permissions and recipients when access level changes from Restricted.
  useEffect(() => {
    if (!isShareModalOpen || accessLevel.value == null) return

    if (accessLevel.value !== RECAP_ACCESS_LEVELS.RESTRICTED) {
      setPendingPermissionUpdates(emptyPendingPermissions)
      setPendingRecipients(emptyPendingRecipients)
      // Clear cached access list so org relation doesn't persist when switching to PUBLIC/PRIVATE
      refetch()
    }
  }, [accessLevel.value, isShareModalOpen, refetch])

  const setPendingUserPermission = useCallback((uuid, relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      users: {
        ...current.users,
        [uuid]: relation,
      },
    }))
  }, [])

  const setPendingGroupPermission = useCallback((uuid, relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      userGroups: {
        ...current.userGroups,
        [uuid]: relation,
      },
    }))
  }, [])

  const setPendingOrgPermission = useCallback((relation) => {
    setPendingPermissionUpdates((current) => ({
      ...current,
      organization: relation,
    }))
  }, [])

  const getUserRelation = useCallback((uuid, serverRelation) => (
    relationToDisplay(pendingPermissionUpdates.users, uuid, serverRelation)
  ), [pendingPermissionUpdates.users])

  const getUserGroupRelation = useCallback((uuid, serverRelation) => (
    relationToDisplay(pendingPermissionUpdates.userGroups, uuid, serverRelation)
  ), [pendingPermissionUpdates.userGroups])

  const getOrganizationRelation = useCallback((serverRelation) => (
    organizationRelationToDisplay(pendingPermissionUpdates.organization, serverRelation)
  ), [pendingPermissionUpdates.organization])

  const addRecipient = useCallback((suggestion) => {
    const recipient = suggestionToPendingRecipient(suggestion)

    if (!recipient) {
      return
    }

    setPendingPermissionUpdates((current) => {
      if (recipient.subjectType === 'user' && Object.hasOwn(current.users, recipient.subjectId)) {
        const users = { ...current.users }
        delete users[recipient.subjectId]
        return { ...current, users }
      }

      if (recipient.subjectType === 'user_group' && Object.hasOwn(current.userGroups, recipient.subjectId)) {
        const userGroups = { ...current.userGroups }
        delete userGroups[recipient.subjectId]
        return { ...current, userGroups }
      }

      return current
    })

    setPendingRecipients((current) => {
      if (current.some(({ id, subjectId, subjectType }) => (
        id === recipient.id || (subjectId === recipient.subjectId && subjectType === recipient.subjectType)
      ))) {
        return current
      }

      return [...current, recipient]
    })
  }, [])

  const removeRecipient = useCallback((id) => {
    setPendingRecipients((current) => current.filter((recipient) => recipient.id !== id))
  }, [])

  const effectiveRecipientCount = useMemo(() => {
    const visibleUsers = accessList.users.filter((user) => (
      getUserRelation(user.uuid, user.relation) !== null
    ))
    const visibleUserGroups = accessList.userGroups.filter((userGroup) => (
      getUserGroupRelation(userGroup.uuid, userGroup.relation) !== null
    ))

    return countEffectiveRecipients({
      users: visibleUsers,
      userGroups: visibleUserGroups,
      ownerUserUuid,
      pendingRecipients,
    })
  }, [
    accessList.userGroups,
    accessList.users,
    getUserGroupRelation,
    getUserRelation,
    ownerUserUuid,
    pendingRecipients,
  ])

  const shareLinkUrl = useMemo(() => {
    const origin = window.location.origin
    const hasShareAccessLevel = Boolean(share?.accessLevel)
    const hasResolvedShareState = hasLocalShareOverride || hasShareAccessLevel || !isRecapShareLoading

    if (!hasResolvedShareState) {
      return null
    }

    const shareAccessLevel = normalizeRecapAccessLevel(share?.accessLevel)
    if (isRecapPublic(share?.accessLevel)) {
      return `${origin}/s/meetings/v2/${share?.uuid}`
    }

    if (shareAccessLevel === RECAP_ACCESS_LEVELS.RESTRICTED) {
      return `${origin}/s/tracked_meetings/r/${share?.uuid}`
    }

    return null
  }, [hasLocalShareOverride, share, isRecapShareLoading])

  const handleCopyShareLink = useCallback(async () => {
    if (!shareLinkUrl) {
      dispatch(showError(I18n.t('publisher.meeting_recap.recap_actions.link_copy_failed')))
      return
    }

    try {
      await navigator.clipboard.writeText(shareLinkUrl)
      dispatch(showSuccess(I18n.t('publisher.action.copied_to_clipboard')))
    } catch {
      dispatch(showError(I18n.t('publisher.meeting_recap.recap_actions.link_copy_failed')))
    }
  }, [shareLinkUrl, dispatch])

  const openEmailFromShare = useCallback(() => {
    closeShareModal()
    openEmailModal()
  }, [closeShareModal, openEmailModal])

  const exportedShare = useMemo(() => {
    if (!share?.accessLevel) {
      return null
    }

    return share
  }, [share])

  const value = useMemo(() => ({
    trackedMeeting: {
      uuid: trackedMeetingUuid,
      name: trackedMeeting?.name,
    },
    owner,
    recapPermission,
    currentUser,
    share: exportedShare,
    shareModal: {
      isOpen: isShareModalOpen,
      open: openShareModal,
      close: closeShareModal,
    },
    emailModal: {
      isOpen: isEmailModalOpen,
      open: openEmailFromShare,
      close: closeEmailModal,
    },
    accessLevel,
    accessList,
    pendingPermissions: pendingPermissionUpdates,
    pendingRecipients,
    permissionUpdates: {
      setPendingUserPermission,
      setPendingGroupPermission,
      setPendingOrgPermission,
      getUserRelation,
      getUserGroupRelation,
      getOrganizationRelation,
    },
    recipientActions: {
      addRecipient,
      removeRecipient,
    },
    shareLink: {
      url: shareLinkUrl,
      copy: handleCopyShareLink,
    },
    recipients: {
      count: effectiveRecipientCount,
      serverCount: recipientCount,
      setCount: setRecipientCount,
      newRecipientsPayload: buildNewRecipientsPayload(pendingRecipients),
    },
    submission: {
      submit: submitPermissions,
      isSubmitting: isSubmittingPermissions,
    },
  }), [
    trackedMeetingUuid,
    trackedMeeting?.name,
    owner,
    recapPermission,
    currentUser,
    exportedShare,
    isShareModalOpen,
    openShareModal,
    closeShareModal,
    isEmailModalOpen,
    openEmailFromShare,
    closeEmailModal,
    accessLevel,
    accessList,
    pendingPermissionUpdates,
    pendingRecipients,
    setPendingUserPermission,
    setPendingGroupPermission,
    setPendingOrgPermission,
    getUserRelation,
    getUserGroupRelation,
    getOrganizationRelation,
    addRecipient,
    removeRecipient,
    shareLinkUrl,
    handleCopyShareLink,
    effectiveRecipientCount,
    recipientCount,
    setRecipientCount,
    submitPermissions,
    isSubmittingPermissions,
  ])

  return (
    <RecapShareContext.Provider value={value}>
      {children}
    </RecapShareContext.Provider>
  )
}
